import React, { useEffect, useMemo, useState } from "react";
import { api, describeApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Lock, Hourglass, Wallet } from "lucide-react";

const usd = (v, d = 2) => (v == null || Number.isNaN(Number(v)) ? "n.d." : `$${Number(v).toFixed(d)}`);

/** Stato della politica budget del tenant (se configurata): plafond, riserva, confermato, disponibile. */
const PolicyBox = ({ p, cap, minCap }) => {
  if (!p) return <div className="text-[10px] text-muted-foreground" data-testid="auth-policy-absent">Nessuna politica budget di tenant configurata: vale il solo minimo richiesto dal preflight.</div>;
  const perDoc = minCap != null ? Number(minCap) * (1 + Number(p.per_document_cap_max_over_minimum_pct || 0) / 100) : null;
  const okDoc = perDoc == null || cap <= perDoc + 1e-9;
  const okPlafond = cap <= Number(p.available_for_next_cap_usd) + 1e-9;
  return (
    <div className={`border p-2 text-[10px] space-y-0.5 ${okDoc && okPlafond ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`} data-testid="auth-policy-box">
      <div className="font-bold flex items-center gap-1"><Wallet size={11} />Politica budget del tenant — {p.label}</div>
      <div>Plafond cumulativo <b>{usd(p.plafond_total_usd)}</b> · confermato <b data-testid="auth-policy-confirmed">{usd(p.confirmed_usd, 4)}</b> ({p.runs_counted} run) · incerto {usd(p.uncertain_usd, 4)} · tetti in corso {usd(p.inflight_caps_usd)} · riserva <b>{usd(p.reserved_usd)}</b>{p.reserved_note ? ` (${p.reserved_note})` : ""}</div>
      <div>Disponibile per questo tetto: <b data-testid="auth-policy-available">{usd(p.available_for_next_cap_usd, 4)}</b>{perDoc != null && <> · massimo per documento: <b data-testid="auth-policy-perdoc">{usd(perDoc)}</b> (= minimo richiesto{Number(p.per_document_cap_max_over_minimum_pct) ? ` + ${p.per_document_cap_max_over_minimum_pct}%` : ""})</>}</div>
      {!okDoc && <div className="text-red-900 font-bold" data-testid="auth-policy-stop-doc">Tetto superiore al massimo per documento: il server rifiuterà l'autorizzazione.</div>}
      {!okPlafond && <div className="text-red-900 font-bold" data-testid="auth-policy-stop-plafond">Tetto oltre il disponibile del plafond: il server rifiuterà l'autorizzazione.</div>}
    </div>
  );
};

/**
 * Passaggio Owner «Autorizza» (P1.7.2): registra SOLO il consenso (POST …/authorize, zero chiamate
 * a pagamento). Tetto = minimo richiesto dal preflight, modificabile solo verso l'alto ed entro la
 * politica budget del tenant (gate anche lato server). L'esecuzione parte esclusivamente con «Avvia».
 */
export const CanonicalAuthorizePanel = ({ jobId, preflight, policy, role, onAuthorized, title }) => {
  const [pf, setPf] = useState(preflight || null);
  const [pfErr, setPfErr] = useState(null);
  const [loadingPf, setLoadingPf] = useState(false);
  const [cap, setCap] = useState("");
  const [checked, setChecked] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => { if (preflight) setPf(preflight); }, [preflight]);
  const b = pf?.budget;
  useEffect(() => { if (b?.minimum_required_cap_usd != null && cap === "") setCap(String(b.minimum_required_cap_usd)); }, [b, cap]);

  const loadPreflight = async () => {
    setLoadingPf(true); setPfErr(null);
    try { const { data } = await api.get(`/reader/jobs/${jobId}/segmented-preflight`, { timeout: 300000 }); setPf(data); }
    catch (e) { setPfErr(describeApiError(e)); }
    finally { setLoadingPf(false); }
  };

  const capNum = Number(cap);
  const minCap = b?.minimum_required_cap_usd;
  const perDoc = policy && minCap != null ? Number(minCap) * (1 + Number(policy.per_document_cap_max_over_minimum_pct || 0) / 100) : null;
  const capValid = useMemo(() => {
    if (!b || Number.isNaN(capNum) || capNum < Number(minCap) - 1e-9) return false;
    if (perDoc != null && capNum > perDoc + 1e-9) return false;
    if (policy && capNum > Number(policy.available_for_next_cap_usd) + 1e-9) return false;
    return true;
  }, [b, capNum, minCap, perDoc, policy]);
  const canSubmit = capValid && checked && !busy && !done;

  const authorize = async () => {
    if (!canSubmit) return;
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post(`/reader/jobs/${jobId}/segmented-run-canonical/authorize`, {
        confirm: true, confirmation_phrase: `CONSENSO OWNER P1.7.2 ${jobId}`, max_cost_usd: Math.round(capNum * 100) / 100,
        expected_max_calls: b?.max_calls ?? null, decision_note: note || `autorizzazione Owner dall'app: tetto ${capNum} (minimo richiesto ${minCap})`,
      }, { timeout: 300000 });
      setDone(data); onAuthorized?.(data);
    } catch (e) { setErr(describeApiError(e)); }
    finally { setBusy(false); }
  };

  if (role !== "owner") {
    return (
      <div className="border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900 flex items-start gap-2" data-testid="hardened-awaiting-owner">
        <Hourglass size={13} className="mt-0.5 shrink-0" />
        <span><b>In attesa di autorizzazione Owner.</b> Solo l'Owner può autorizzare la lettura segmentata P1.7.2 (nessun pulsante disponibile per questo ruolo; il server rifiuta con 403).</span>
      </div>
    );
  }

  return (
    <div className="border-2 border-indigo-400 p-2 sm:p-3 space-y-2 text-[11px]" data-testid="canonical-authorize-panel">
      <div className="font-bold flex items-center gap-2"><ShieldCheck size={14} className="text-indigo-700" />{title || "Passaggio Owner 1/2 — Autorizza la lettura segmentata P1.7.2"}</div>
      <div className="text-[10px] text-muted-foreground">«Autorizza» registra soltanto il consenso (piano canonico persistito, tetto assoluto, frase di avvio congelata): <b>nessuna chiamata a pagamento</b>. Le chiamate al provider iniziano esclusivamente con «Avvia» (passaggio 2/2, frase esatta).</div>
      {!b && (
        <div className="space-y-1">
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={loadPreflight} disabled={loadingPf} data-testid="authorize-load-preflight-btn">
            {loadingPf ? "Preflight in corso (solo lettura locale del PDF)…" : "Calcola tetto minimo richiesto (preflight, zero chiamate)"}
          </Button>
          {pfErr && <div className="text-red-900 border border-red-300 bg-red-50 p-1.5" data-testid="authorize-preflight-error">{pfErr.message}</div>}
        </div>
      )}
      {b && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-0.5" data-testid="authorize-budget-grid">
            <div>Tipico stimato: <b>{usd(b.typical_total_usd, 4)}</b></div>
            <div>Worst case: <b>{usd(b.worst_case_total_usd, 4)}</b></div>
            <div>Margine: <b>{b.safety_margin_pct}%</b></div>
            <div>Minimo richiesto: <b data-testid="authorize-min-cap">{usd(b.minimum_required_cap_usd)}</b> · POST max {b.max_calls}</div>
          </div>
          <PolicyBox p={policy} cap={capNum} minCap={minCap} />
          <label className="flex flex-wrap items-center gap-2">
            <span>Tetto assoluto Owner (USD, ≥ minimo{perDoc != null ? `, ≤ ${perDoc.toFixed(2)}` : ""}):</span>
            <input type="number" step="0.01" min={minCap} value={cap} onChange={(e) => setCap(e.target.value)} disabled={busy || !!done}
              className="border border-border px-2 py-1 w-28 font-mono text-sm" data-testid="authorize-cap-input" />
            {!capValid && <span className="text-red-800 text-[10px]" data-testid="authorize-cap-invalid">tetto non ammesso</span>}
          </label>
          <label className="flex items-start gap-2">
            <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} disabled={busy || !!done} className="mt-0.5 w-5 h-5 shrink-0" data-testid="authorize-owner-checkbox" />
            <span>Confermo come Owner la predisposizione dell'autorizzazione one-shot (tetto assoluto {usd(capNum)}, {b.max_calls} POST max, esito HUMAN_REVIEW_REQUIRED). Nessuna chiamata a pagamento viene eseguita ora.</span>
          </label>
          <input type="text" value={note} onChange={(e) => setNote(e.target.value)} disabled={busy || !!done} placeholder="Nota di decisione (facoltativa, registrata nell'autorizzazione)"
            className="w-full border border-border px-2 py-1.5 text-xs" data-testid="authorize-note-input" />
          <Button onClick={authorize} disabled={!canSubmit} className="w-full min-h-[40px] bg-indigo-700 hover:bg-indigo-800" data-testid="authorize-button">
            {canSubmit ? <ShieldCheck size={14} className="mr-1" /> : <Lock size={14} className="mr-1" />}
            {busy ? "Registrazione consenso…" : "Autorizza (registra il consenso — nessuna chiamata a pagamento)"}
          </Button>
        </>
      )}
      {done && (
        <div className="border border-emerald-400 bg-emerald-50 p-2 text-emerald-900" data-testid="authorize-success">
          <b>Autorizzazione registrata</b> · run <span className="font-mono">{done.logical_reader_run_id}</span> · tetto {usd(done.max_cost_usd)} · POST max {done.max_calls} · chiamate provider eseguite: <b>{done.provider_calls_made ?? 0}</b> · scade {String(done.expires_at || "").slice(0, 16)}. Ora è disponibile il passaggio 2/2 «Avvia».
        </div>
      )}
      {err && (
        <div className="border border-red-400 bg-red-50 p-2 text-red-900 space-y-0.5" data-testid="authorize-error">
          <div className="font-bold">Autorizzazione non registrata{err.status ? ` — HTTP ${err.status}` : ""}{err.code ? ` · ${err.code}` : ""}</div>
          <div>{err.message}</div>
        </div>
      )}
    </div>
  );
};
